import React, { useState } from "react";
import { 
  Spinner, 
  SpinnerSize, 
  Stack,
  FontIcon } from "@fluentui/react";

import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Callout } from '@fluentui/react/lib/Callout';
import { DirectionalHint } from '@fluentui/react/lib/Callout';
import { useBoolean, useId } from '@fluentui/react-hooks';

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import supersub from "remark-supersub";
import rehypeRaw from "rehype-raw";

import { setPreferredResponseMode, setShowedStartupScreen } from "../Utils/utils";
import styles from "./StartupScreen.module.css";

type StartupScreenProps = { 
  isAssistantAPILoading: boolean;
  onStartupComplete?: () => void;
};

export const StartupScreen: React.FC<StartupScreenProps> = ({  
  isAssistantAPILoading,
  onStartupComplete
}) => {
  const [selectedMode, setSelectedMode] = useState<string | null>(null);

  const handleContinue = () => {
    if (selectedMode) {
      setPreferredResponseMode(selectedMode);
      setShowedStartupScreen(true);
      // Notify parent component that startup is complete
      if (onStartupComplete) {
        onStartupComplete();
      }
    }
  };

  return (
    <Stack className={styles.startupRoot} data-tour="startup-screen">                  
      {isAssistantAPILoading ? (        
        <div className={styles.loadingContainer}>
          <Spinner size={SpinnerSize.large} label="Establishing connection..." />
        </div>
      ) : (
        <>
          <h1 className={styles.startupTitle}>
            PICK YOUR DEFAULT <span className={styles.dataText}>&nbsp;RESPONSE MODE</span>
          </h1>

          <h1 className={styles.startupSubtitle}>
            You can change this anytime in the settings menu.
          </h1>
          
          <div className={styles.container}>
            <main className={styles.mainContent}>
              <section className={styles.cards} data-tour="response-modes">
                <ResponseModeCard
                  title="Simple"
                  modelName="GPT-5 Nano"
                  features={[
                    "Ideal for Level 1 support", 
                    "Short, concise answers",
                    "Step-by-step action plans",
                    "No detailed explanations",
                    "Limited to L1 permission scope"
                  ]}
                  additionalCssClass={styles.simple}
                  exampleHeadline="Simple response"
                  exampleResponse="I see that you have trouble with an MSR (Magnetic Stripe Reader) device.

1. Check that all cabling is properly connected and the touchscreen is responsive.
2. Reboot the system if not already done.
3. If the MSR does not read any cards, clean the device with a tissue.
4. If only one card is not working, test it on another till to rule out a card issue.
5. If there is an OPOS error message, note the exact error and check the registry setting for the MSR device. The correct registry value should be:
   - 'ProBaseStore': DN_MSR_BA9x_USB_UDM
   - If not working, try: DN_MSR_BA83_USB_UDM
6. If there is a visible defect, escalate to Field Service for hardware replacement.

Please confirm if you see any OPOS error message or if there is a visible defect on the device."
                  isSelected={selectedMode === "simple"}
                  onSelect={() => setSelectedMode("simple")}
                />          
                <ResponseModeCard
                  title="Standard"
                  modelName="GPT-5 Mini"
                  isRecommended={true}
                  features={[
                    "Ideal for Level 2/3 support and PS",
                    "Balanced detail and clarity",
                    "Includes troubleshooting steps",
                    "More technical depth",
                    "Comprehensive explanations"
                  ]}
                  additionalCssClass={styles.standard}
                  exampleHeadline="Standard response"
                  exampleResponse="To resolve an MSR (Magnetic Stripe Reader) device issue, please follow these steps:

1. **Check the basics:**
   - Ensure the cabling is fine and the MSR is properly connected.
   - Confirm the touchscreen is responsive and the correct model is used.
   - Note any error messages that appear.
   - Try rebooting the system.

2. **If the MSR does not read any card:**
   - Clean the device with a tissue.
   - If only one card is not working, test that card on another till to rule out a card issue.

3. **Check registry settings:**
   - The correct registry setting for the MSR device should be:
     - `ProBaseStore: DN_MSR_BA9x_USB_UDM`
   - If this does not work, try:
     - `DN_MSR_BA83_USB_UDM`

4. **If there is a visible defect or the above steps do not resolve the issue:**
   - A field service technician may need to replace the MSR module.

5. **If you receive an OPOS error message:**
   - Check the registry or OPOS settings as described above.
   - If the issue persists, document the OPOS message for further support.

If you need more detailed instructions for registry or OPOS configuration, or if you see a specific error message, please provide that information for further guidance.

<u>See full MSR troubleshooting guide</u>

Is there a visible defect on the MSR device, or do you see an error message on the screen?"
                  isSelected={selectedMode === "standard"}
                  onSelect={() => setSelectedMode("standard")}
                />          
                <ResponseModeCard
                  title="Enhanced"
                  modelName="GPT-4.1"
                  features={[                    
                    "For undocumented scenarios",
                    "Distinguishes verified vs. inferred information",
                    "Warns about high-impact system actions",
                    "Requires central platform approval checks",
                    "Uses enhanced reasoning"
                  ]}
                  additionalCssClass={styles.enhanced}
                  exampleHeadline="Enhanced response"
                  exampleResponse="Summary (short):

- Verified first checks (do these now):
  1) Confirm cabling and physical connection of the MSR.  
  2) Confirm which till / display model you have (BA73 = PS/2 MSR, BA83 = USB MSR, mobile moPOS = built-in MSR).  
  3) Note the exact error message shown on the POS.  
  4) Reboot the till if not already done, then in the POS go to Utilities ? Reload Parameters.  

Step-by-step troubleshooting (with source pointers):

1) Basic verification (verified, from IKEA FSO MSR troubleshooting):
   - Check cabling, confirm the touchscreen model, capture the full error text, ensure touchscreen is responsive, and confirm a reboot was already tried. These are mandatory first checks. <u>IKEA FSO: POS MSR troubleshooting</u>

2) Quick local fixes to try (factual actions from the documentation):
   - Clean the MSR head with a tissue if the MSR does not read any cards. If only one card fails, try that card on another till to verify the card. <u>IKEA FSO: POS MSR troubleshooting</u>
   - From the POS application: Utilities ? Reload Parameters to reopen devices (this reclaims peripherals). <u>IKEA iPos Project - Technical HandBook for Rollout Installation iPOS 4 FOOD - V 1.0.pdf</u>
   - Sign out and sign in (workaround used for intermittent MSR alerts). See Jira <u>IKEAPM-142</u> where sign-out/sign-in or waiting makes errors vanish.

3) Test the MSR via the device-config tool (verified):
   - Use the TPPosDeviceConfiguration (POS Device Configuration) tool and run the MSR Test. The tool will prompt you to swipe a card and should show the track data in the test box on success. If test fails, adjust the device properties (port / SoName) and re-test. <u>iPOS_A30_POS_Device_Configuration_Tool.docx</u>

4) Registry / OPOS / device name settings (verified reference + configuration values to check):
   - Ensure the ProBaseStore registry / OPOS setting for the MSR is correct. Recommended logical names: 'ProBaseStore': DN_MSR_BA9x_USB_UDM. If that does not work, try DN_MSR_BA83_USB_UDM. (These logical names are what the site KB recommends to try). <u>IKEA FSO: POS MSR troubleshooting</u>
   - If the MSR is on a BA73: BA73 uses a PS/2-connected MSR (not USB) and OPOS configuration must be adapted accordingly (do not assume USB). <u>IKEA iPos Project - Technical HandBook for Rollout Installation iPOS 4 FOOD - V 1.0.pdf</u>

5) Serial/COM port issues (factual procedure used in EasyExpress handbook):
   - If the MSR (or other serial device) is on a COM port and the port is wrong or enumerated incorrectly, you can run a COM reset using WNDevcon. Recommended procedure (ADMIN cmd):


cd C:\Retail\Hardware   (or C:\Retail\Software\Opos\Bin)
WNDevcon.exe remove *PNP0501*
(restart the PC)

   - After reboot, re-run the TPPosDeviceConfiguration test and reload parameters in POS. Note: changing COM/registry is a system-impacting action - confirm with IMAC/Rollout before doing it. <u>IKEA_Technical Handbook for Easy Express (non Cash) installation.docx</u>

6) If a visible hardware defect is present or device swap is needed (factual flow):
   - The MSR is a separate spare part mounted at the side of the touchscreen and can be exchanged individually. If a visible defect is reported, field service with a spare part should be dispatched per the flow. <u>IKEA FSO: POS MSR troubleshooting</u>
When to escalate / open a ticket (verified):
- If the MSR test in the POS Device Configuration tool fails after checking cabling, correct registry/OPOS logical name, trying the alternate logical name, and doing a COM reset (if applicable), escalate to IMAC / Rollout support or open a field service ticket. Rollout support contact (for onsite/install escalation): rollout.ikea@dieboldnixdorf.com / +44 20 3684 7708 (from the rollout handbook). <u>IKEA iPOS New Store Openings Service Description.docx</u>

Distinguishing facts vs inference (short):
- Verified factual information: the checklists, registry names to try (DN_MSR_BA9x_USB_UDM and DN_MSR_BA83_USB_UDM), BA73 uses PS/2, TPPosDeviceConfiguration test behavior, WNDevcon COM reset command, Reload Parameters location. (Cited above.)
- Probabilistic inference: if cleaning the head and reloading parameters fixes the issue, the root cause is likely dirt or a temporary OPOS claim problem (inference based on typical behavior described in KB).  
- Unknown / unverifiable here: whether the MSR hardware itself is faulty onsite - that can only be confirmed by swapping the MSR or testing on another till.

Safety / governance note (important):
- Registry edits and COM resets can affect device behaviour and should be approved/coordinated with IMAC/Rollout (or local process) before you proceed. The documentation shows these operations are used in field support but they are impactful. <u>IKEA_Technical Handbook for Easy Express (non Cash) installation.docx</u>  

If you want, I can now:  
- Give the exact registry path and keys to check for the MSR on Windows 7 vs Windows 10 systems; or  
- Provide the step to run the TPPosDeviceConfiguration tool and what exact SoName/Port to try; or  
- Create a short checklist you can send to the store technician to perform onsite.  

Which of these applies for your case?"
                  isSelected={selectedMode === "enhanced"}
                  onSelect={() => setSelectedMode("enhanced")}
                />          
              </section>
            </main>
            
            <div className={styles.continueButtonContainer}>
              <PrimaryButton
                text="Continue"
                onClick={handleContinue}
                disabled={!selectedMode}
                className={styles.continueButton}
              />
            </div>
          </div>
        </>      
      )}
    </Stack>
  );
};

interface ResponseModeCardProps {  
  title: string;  
  modelName: string;
  features: string[];
  exampleHeadline: string;
  exampleResponse: string;  
  additionalCssClass: string;
  isSelected: boolean;
  onSelect: () => void;
  isRecommended?: boolean;
}

const ResponseModeCard: React.FC<ResponseModeCardProps> = ({ 
  title, 
  modelName,
  features, 
  exampleHeadline, 
  exampleResponse,   
  additionalCssClass, 
  isSelected,
  onSelect,
  isRecommended
}) => {

  const buttonId = useId("button");
  const [teachingBubbleVisible, { toggle: toggleTeachingBubbleVisible }] = useBoolean(false);

  const handleCardClick = (e: React.MouseEvent) => {
    // Prevent selection when clicking on the button or teaching bubble
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    onSelect();
  };

  return (
    <Stack 
      className={`${styles.responseModeCard} ${additionalCssClass} ${isSelected ? styles.selected : ''}`}
      onClick={handleCardClick}
      style={{ cursor: 'pointer' }}
      tabIndex={0}
      role="radio"
      aria-checked={isSelected}
    >      
      <h2 className={styles.cardTitle}>{title}</h2>
      <span className={styles.modelName}>{modelName}</span>
      {isRecommended && (
        <span className={styles.recommendedBadge}>⭐ Recommended for Product Management</span>
      )}
      {features.map((feature, idx) => {
              return (
                <span                  
                  tabIndex={0}                  
                  key={idx}                  
                  className={styles.feature}
                >
                  <FontIcon className={styles.checkmark} iconName="CheckMark" /> 
                  {feature}
                </span>
              );
            })}

      <DefaultButton
        id={buttonId}
        onClick={toggleTeachingBubbleVisible}
        text="Example Response"
        className={styles.exampleButton}
      />

      {teachingBubbleVisible && (
        <Callout          
          directionalHint={DirectionalHint.bottomCenter}
          target={`#${buttonId}`}
          onDismiss={toggleTeachingBubbleVisible}
          beakWidth={30}
        >
          <div className={styles.exampleContainerRoot}>
            <h2>{exampleHeadline}</h2>
             <ReactMarkdown
                remarkPlugins={[remarkGfm, supersub]}
                rehypePlugins={[rehypeRaw]}
                children={exampleResponse}
                className={styles.exampleContainer}                
              />
           </div>
        </Callout>
      )}
    </Stack>
  );
};